import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface StarDistributionBarProps {
  /** Counts per star rating, indexed `[1★, 2★, 3★, 4★, 5★]`. */
  distribution: number[];
  /**
   * When true, renders a compact mini bar (smaller text, thinner rows) used
   * on the overview stat cards. Detail pages use the full-size variant.
   */
  compact?: boolean;
}

export function StarDistributionBar({ distribution, compact = false }: StarDistributionBarProps) {
  const total = distribution.reduce((sum, n) => sum + n, 0);

  return (
    <div className={cn("space-y-1", compact && "space-y-0.5")}>
      {[5, 4, 3, 2, 1].map((star) => {
        const count = distribution[star - 1] ?? 0;
        const percent = total === 0 ? 0 : (count / total) * 100;
        return (
          <div
            key={star}
            className={cn("flex items-center gap-2 text-sm", compact && "gap-1.5 text-xs")}
          >
            <div
              className={cn(
                "flex items-center gap-0.5 w-6 tabular-nums text-muted-foreground",
                compact && "w-5"
              )}
            >
              {star}
              <Star
                className={cn("h-3 w-3 fill-amber-400 text-amber-400", compact && "h-2.5 w-2.5")}
              />
            </div>
            <div
              className={cn("flex-1 h-2 rounded-full bg-muted overflow-hidden", compact && "h-1.5")}
            >
              <div
                className="h-full bg-amber-400 transition-[width] duration-300 ease-out"
                style={{ width: `${percent}%` }}
              />
            </div>
            <div
              className={cn("w-8 text-right tabular-nums text-muted-foreground", compact && "w-6")}
            >
              {count}
            </div>
          </div>
        );
      })}
    </div>
  );
}
